/**
 * Project Inputs Component
 * Handles area, thickness, facility life, and destination city inputs
 */

import React from 'react'
import { UnitSystem } from '../types/calculator.types'
import { CityResult } from '../types/cityAutosuggest.types'
import { getFormattedCityNames } from '../utils/freightCities'
import CityAutosuggest from './CityAutosuggest'
import styles from './ProjectInputs.module.css'

interface ProjectInputsProps {
  unitSystem: UnitSystem
  area: number
  thickness: number
  facilityLife: number
  deliveryCity: string
  onAreaChange: (value: number) => void
  onThicknessChange: (value: number) => void
  onFacilityLifeChange: (value: number) => void
  onDeliveryCityChange: (value: string) => void
  onCitySelect?: (city: CityResult) => void
  errors?: {
    area?: string
    thickness?: string
    facilityLife?: string
    deliveryCity?: string
  }
  disabled?: boolean
  className?: string
  enableAutosuggest?: boolean
  citySearchApiEndpoint?: string
  debounceMs?: number
  minCharacters?: number
  maxSuggestions?: number
}

const ProjectInputs: React.FC<ProjectInputsProps> = ({
  unitSystem,
  area,
  thickness,
  facilityLife,
  deliveryCity,
  onAreaChange,
  onThicknessChange,
  onFacilityLifeChange,
  onDeliveryCityChange,
  onCitySelect,
  errors = {},
  disabled = false,
  className = '',
  enableAutosuggest = true,
  citySearchApiEndpoint,
  debounceMs = 300,
  minCharacters = 3,
  maxSuggestions = 10
}) => {
  const cityOptions = getFormattedCityNames()

  const areaUnit = unitSystem === 'imperial' ? 'sq ft' : 'sq m'
  const thicknessUnit = unitSystem === 'imperial' ? 'inches' : 'mm'

  return (
    <div className={`${styles.container} ${className}`}>
      <h3 className={styles.sectionTitle}>Project Specifications</h3>

      {/* Delivery City Input */}
      <div className={styles.inputGroup}>
        <label htmlFor="deliveryCity" className={styles.label}>
          Destination City
          <span className={styles.required}>*</span>
        </label>
        <span className={styles.helpText}>
          Any major city in North America (example: Dallas, TX or Vancouver, BC). Determines output currency and freight cost.
        </span>
        
        {enableAutosuggest ? (
          <CityAutosuggest
            value={deliveryCity}
            onChange={onDeliveryCityChange}
            onSelect={onCitySelect}
            placeholder="Enter city name or select from suggestions"
            disabled={disabled}
            className={styles.input}
            enableAutosuggest={enableAutosuggest}
            apiEndpoint={citySearchApiEndpoint}
            debounceMs={debounceMs}
            minCharacters={minCharacters}
            maxSuggestions={maxSuggestions}
            error={errors.deliveryCity}
            data-testid="delivery-city-autosuggest"
          />
        ) : (
          <>
            <input
              id="deliveryCity"
              type="text"
              value={deliveryCity}
              onChange={(e) => onDeliveryCityChange(e.target.value)}
              placeholder="Enter city name or select from list"
              list="city-options"
              disabled={disabled}
              className={`${styles.input} ${errors.deliveryCity ? styles.inputError : ''}`}
              aria-describedby={errors.deliveryCity ? 'deliveryCity-error' : undefined}
              data-testid="delivery-city-input"
            />
            <datalist id="city-options">
              {cityOptions.map((city) => (
                <option key={city} value={city} />
              ))}
            </datalist>
            {errors.deliveryCity && (
              <span id="deliveryCity-error" className={styles.errorText} role="alert">
                {errors.deliveryCity}
              </span>
            )}
          </>
        )}
      </div>

      {/* Area Input */}
      <div className={styles.inputGroup}>
        <label htmlFor="area" className={styles.label}>
          Total Area ({areaUnit})
          <span className={styles.required}>*</span>
        </label>
        <span className={styles.helpText}>
          {areaUnit === 'sq ft'
            ? 'Enter the total square footage of the slab.'
            : 'Enter the total slab area in square meters.'}
        </span>
        <input
          id="area"
          type="number"
          value={area || ''}
          onChange={(e) => onAreaChange(Number(e.target.value))}
          placeholder={`Enter area in ${areaUnit}`}
          min="1"
          max="1000000"
          step="1"
          disabled={disabled}
          className={`${styles.input} ${errors.area ? styles.inputError : ''}`}
          aria-describedby={errors.area ? 'area-error' : undefined}
        />
        {errors.area && (
          <span id="area-error" className={styles.errorText} role="alert">
            {errors.area}
          </span>
        )}
      </div>

      {/* Thickness Input */}
      <div className={styles.inputGroup}>
        <label htmlFor="thickness" className={styles.label}>
          Slab Thickness ({thicknessUnit})
          <span className={styles.required}>*</span>
        </label>
        <span className={styles.helpText}>
          {thicknessUnit === 'inches'
            ? 'Enter the slab thickness in inches.'
            : 'Enter the slab thickness in millimeters.'}
        </span>
        <input
          id="thickness"
          type="number"
          value={thickness || ''}
          onChange={(e) => onThicknessChange(Number(e.target.value))}
          placeholder={`Enter thickness in ${thicknessUnit}`}
          min={unitSystem === 'imperial' ? '1' : '1'}
          max={unitSystem === 'imperial' ? '1000' : '25000'}
          step={unitSystem === 'imperial' ? '0.25' : '1'}
          disabled={disabled}
          className={`${styles.input} ${errors.thickness ? styles.inputError : ''}`}
          aria-describedby={errors.thickness ? 'thickness-error' : undefined}
        />
        {errors.thickness && (
          <span id="thickness-error" className={styles.errorText} role="alert">
            {errors.thickness}
          </span>
        )}
        
      </div>

      {/* Facility Life Input */}
      <div className={styles.inputGroup}>
        <label htmlFor="facilityLife" className={styles.label}>
          Facility Operational Life (years)
          <span className={styles.required}>*</span>
        </label>
        <span className={styles.helpText}>
          Design lifespan of the facility.
        </span>
        <input
          id="facilityLife"
          type="number"
          value={facilityLife || ''}
          onChange={(e) => onFacilityLifeChange(Number(e.target.value))}
          placeholder="Enter facility life in years"
          min="1"
          max="100"
          step="1"
          disabled={disabled}
          className={`${styles.input} ${errors.facilityLife ? styles.inputError : ''}`}
          aria-describedby={errors.facilityLife ? 'facilityLife-error' : undefined}
        />
        {errors.facilityLife && (
          <span id="facilityLife-error" className={styles.errorText} role="alert">
            {errors.facilityLife}
          </span>
        )}
      </div>


    </div>
  )
}

export default ProjectInputs